/**
 * resize-move — drag a per-corner / per-edge handle to resize an overlay
 * bounding box on the PDF preview pane.
 *
 * Companion to `drag-move.ts`. drag-move handles whole-box pointerdown;
 * this module handles pointerdown on the 8 `.editor-resize-handle` children
 * of an overlay box. Both controllers share the same overlay container —
 * dispatch by event-target so they don't interfere.
 *
 * Every overlay box exposes 8 handles (nw, n, ne, e, se, s, sw, w).
 * Dragging a corner handle resizes width AND height; an edge handle resizes
 * one dimension only.
 *
 * Pure helpers (`computeResizedBbox`, `enforceMinSize`) run in node tests;
 * the controller is browser-only.
 *
 * Wire shape: each completed resize emits one `set-bbox` SPA op with
 * before / after [x, y, w, h] in PDF user-space (same shape as drag-move).
 *
 * Esc during a resize cancels and restores the original size.
 *
 * Migrated to TypeScript 2026-06-24 as part of rule-#24 wave
 * (ts-unit-testability-seams-260623 Phase 2).
 *
 * @module resize-move
 */
import { pixelDeltaToPdf, clampBboxToPage } from "./drag-move.js";
import { GestureController } from "./gesture-controller.js";
const MIN_RESIZE_PX = 3;
const MIN_BBOX_PT = 4; // 4 PDF points minimum width/height
/**
 * The eight handle direction tokens. Order chosen so the array maps
 * deterministically to the 8 DOM children of each overlay box.
 */
export const RESIZE_HANDLES = Object.freeze(["nw", "n", "ne", "e", "se", "s", "sw", "w"]);
/** Whether a handle anchors the WEST edge (i.e. dragging it changes x). */
function affectsWest(dir) { return dir === "nw" || dir === "w" || dir === "sw"; }
/** Whether a handle anchors the EAST edge (changes width but not x). */
function affectsEast(dir) { return dir === "ne" || dir === "e" || dir === "se"; }
/**
 * Whether a handle anchors the NORTH edge (in PDF user-space, top edge).
 * Visual NORTH = larger y in PDF user-space (pixelDeltaToPdf already inverted).
 */
function affectsNorth(dir) { return dir === "nw" || dir === "n" || dir === "ne"; }
/** Whether a handle anchors the SOUTH edge. */
function affectsSouth(dir) { return dir === "sw" || dir === "s" || dir === "se"; }
/**
 * Apply a PDF-user-space delta to a bbox according to which handle is being
 * dragged. Returns a fresh [x, y, w, h] array.
 *
 * Coordinate convention: bboxes are PDF user-space — origin bottom-left,
 * y grows up, units = points. The handle direction tokens are *visual*
 * (north = top of the screen). The pdfDelta is in PDF user-space (already
 * y-inverted by `pixelDeltaToPdf`).
 *
 * Visual NORTH handle pulls the top edge → in PDF space that means the y+h
 * (top-right corner's y) moves with the delta. We track `top = y + h` and
 * update accordingly so width/height stay non-negative.
 */
export function computeResizedBbox(bbox, dir, pdfDelta) {
    if (!Array.isArray(bbox) || bbox.length !== 4)
        return bbox;
    const dx = (pdfDelta && Number.isFinite(pdfDelta.dx)) ? pdfDelta.dx : 0;
    const dy = (pdfDelta && Number.isFinite(pdfDelta.dy)) ? pdfDelta.dy : 0;
    let left = bbox[0];
    let bottom = bbox[1];
    const w = bbox[2];
    const h = bbox[3];
    let right = left + w;
    let top = bottom + h;
    if (affectsWest(dir))
        left += dx;
    if (affectsEast(dir))
        right += dx;
    // Visual NORTH = larger y in PDF user-space (because pixelDeltaToPdf
    // already inverted). So north handle moves `top`.
    if (affectsNorth(dir))
        top += dy;
    if (affectsSouth(dir))
        bottom += dy;
    // Re-derive [x, y, w, h] from the four edges. Allow inversion (user
    // dragged past the opposite edge) — flip sides so width/height stay
    // non-negative.
    if (right < left)
        [left, right] = [right, left];
    if (top < bottom)
        [bottom, top] = [top, bottom];
    return [left, bottom, right - left, top - bottom];
}
/**
 * Enforce a minimum bbox size (width AND height). When the user drags a
 * handle past the opposite edge, computeResizedBbox returns a degenerate
 * (zero-area or near-zero-area) bbox; the floor here keeps it pickable.
 *
 * @param bbox - [x, y, w, h]
 * @param minSize - Default 4 PDF points (≈ 0.06" on US-Letter).
 */
export function enforceMinSize(bbox, minSize) {
    if (!Array.isArray(bbox) || bbox.length !== 4)
        return bbox;
    const m = Number.isFinite(minSize) && minSize > 0 ? minSize : MIN_BBOX_PT;
    const x = bbox[0] ?? 0;
    const y = bbox[1] ?? 0;
    const w = bbox[2] ?? 0;
    const h = bbox[3] ?? 0;
    return [x, y, Math.max(m, w), Math.max(m, h)];
}
/**
 * Controller for drag-to-resize overlay bounding boxes via per-edge handles.
 *
 * @param hooks - Dependency injection for overlay container and callbacks.
 * @returns Handle with `destroy()` and `_isResizingForTest()`.
 */
export function ResizeMoveController(hooks) {
    if (!hooks || !hooks.overlayContainer || typeof hooks.onCommit !== "function") {
        throw new Error("ResizeMoveController: overlayContainer + onCommit required");
    }
    const root = hooks.overlayContainer;
    const doc = root.ownerDocument ?? (typeof document !== "undefined" ? document : null);
    if (!doc)
        throw new Error("ResizeMoveController: no ownerDocument");
    /**
     * Resize gesture on .editor-resize-handle children of an overlay bbox.
     *
     * Context shape: { dir, structId, originalBbox, boxEl, originalCss, origin }
     *
     * Side effects:
     *   on engage:   adds .is-resizing to the box element.
     *   on move:     live-resizes box CSS (via liveResizeCss).
     *   on commit:   removes .is-resizing; clamps to page; fires hooks.onCommit
     *                if the bbox actually changed.
     *   on cancel:   removes .is-resizing; restores original CSS.
     *
     * Uses capture-phase pointerdown so it claims handle clicks before
     * drag-move's bubble-phase listener on the same overlay tree can
     * interpret them as whole-box drag.
     */
    class ResizeGesture extends GestureController {
        onPointerDown(ev) {
            if (ev.button !== 0)
                return null;
            const evTarget = ev.target;
            const handle = evTarget?.closest?.(".editor-resize-handle");
            if (!handle)
                return null;
            const dirRaw = handle.dataset?.dir;
            if (!dirRaw || !RESIZE_HANDLES.includes(dirRaw))
                return null;
            const dir = dirRaw;
            const boxEl = handle.closest(".editor-overlay-bbox");
            if (!boxEl)
                return null;
            const structId = boxEl.dataset?.structId;
            if (!structId)
                return null;
            const bboxesById = (typeof hooks.getBboxesById === "function") ? hooks.getBboxesById() : new Map();
            const original = bboxesById.get(structId);
            if (!original || original.length !== 4)
                return null;
            const rect = root.getBoundingClientRect();
            const ctx = {
                dir,
                structId,
                originalBbox: original.slice(),
                boxEl,
                originalCss: {
                    left: parseFloat(boxEl.style.left) || 0,
                    top: parseFloat(boxEl.style.top) || 0,
                    width: parseFloat(boxEl.style.width) || boxEl.offsetWidth || 0,
                    height: parseFloat(boxEl.style.height) || boxEl.offsetHeight || 0,
                },
                origin: { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
            };
            boxEl.classList.add("is-resizing");
            // capture-phase claim: stop drag-move from also seeing this pointerdown
            ev.stopPropagation?.();
            ev.preventDefault?.();
            return ctx;
        }
        onPointerMove(context, ev) {
            const rect = root.getBoundingClientRect();
            const cur = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
            const dxPx = cur.x - context.origin.x;
            const dyPx = cur.y - context.origin.y;
            // Live-translate CSS for instant visual feedback. Use canvas-px math
            // here so we don't need a scale to render — the commit path converts
            // to PDF pts.
            const cssOut = liveResizeCss(context.dir, context.originalCss, { dx: dxPx, dy: dyPx });
            context.boxEl.style.left = `${cssOut.left}px`;
            context.boxEl.style.top = `${cssOut.top}px`;
            context.boxEl.style.width = `${cssOut.width}px`;
            context.boxEl.style.height = `${cssOut.height}px`;
        }
        onCommit(context, ev) {
            const rect = root.getBoundingClientRect();
            const cur = ev
                ? { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
                : context.origin;
            const dxPx = cur.x - context.origin.x;
            const dyPx = cur.y - context.origin.y;
            context.boxEl.classList.remove("is-resizing");
            if (Math.abs(dxPx) < MIN_RESIZE_PX && Math.abs(dyPx) < MIN_RESIZE_PX) {
                // Restore CSS — treat as a click.
                context.boxEl.style.left = `${context.originalCss.left}px`;
                context.boxEl.style.top = `${context.originalCss.top}px`;
                context.boxEl.style.width = `${context.originalCss.width}px`;
                context.boxEl.style.height = `${context.originalCss.height}px`;
                return;
            }
            const scale = (typeof hooks.getScale === "function") ? hooks.getScale() : 1;
            const pdfDelta = pixelDeltaToPdf({ dx: dxPx, dy: dyPx }, scale);
            const after = enforceMinSize(computeResizedBbox(context.originalBbox, context.dir, pdfDelta));
            const pageSize = (typeof hooks.getPageSize === "function") ? hooks.getPageSize() : null;
            const clamped = pageSize ? clampBboxToPage(after, pageSize) : after;
            const same = (clamped[0] === context.originalBbox[0] &&
                clamped[1] === context.originalBbox[1] &&
                clamped[2] === context.originalBbox[2] &&
                clamped[3] === context.originalBbox[3]);
            if (!same) {
                hooks.onCommit({
                    structId: context.structId,
                    before: context.originalBbox.slice(),
                    after: clamped,
                });
                // host-side throws bubble through GestureController, which logs
                // at warn — controller has no recovery path beyond resetting,
                // which the base does automatically.
            }
        }
        onCancel(context) {
            context.boxEl.classList.remove("is-resizing");
            context.boxEl.style.left = `${context.originalCss.left}px`;
            context.boxEl.style.top = `${context.originalCss.top}px`;
            context.boxEl.style.width = `${context.originalCss.width}px`;
            context.boxEl.style.height = `${context.originalCss.height}px`;
        }
    }
    const gesture = new ResizeGesture({
        name: "resize-move",
        target: root,
        document: doc,
        // Capture-phase so we beat drag-move's bubble-phase pointerdown
        // listener for clicks that land on .editor-resize-handle.
        usePointerDownCapture: true,
    });
    return {
        destroy() {
            gesture.destroy();
        },
        _isResizingForTest() { return gesture.state === "ACTIVE"; },
    };
}
/**
 * Compute new CSS-px geometry for live-resize feedback. The CSS coordinate
 * frame is top-left-origin (y grows down), so visual NORTH handle decreases
 * `top` and increases `height`.
 */
export function liveResizeCss(dir, originalCss, pixelDelta) {
    const dx = (pixelDelta && Number.isFinite(pixelDelta.dx)) ? pixelDelta.dx : 0;
    const dy = (pixelDelta && Number.isFinite(pixelDelta.dy)) ? pixelDelta.dy : 0;
    let left = originalCss.left;
    let top = originalCss.top;
    let right = originalCss.left + originalCss.width;
    let bottom = originalCss.top + originalCss.height;
    if (affectsWest(dir))
        left += dx;
    if (affectsEast(dir))
        right += dx;
    if (affectsNorth(dir))
        top += dy;
    if (affectsSouth(dir))
        bottom += dy;
    if (right < left)
        [left, right] = [right, left];
    if (bottom < top)
        [top, bottom] = [bottom, top];
    return { left, top, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
}
